import { mapState } from 'pinia';
import stores from 'store/modules';
import { toLower, get } from 'lodash';
import FeatureablesMixin from './featureables';

/** Exist keys for plans - old billing plans
 * "base"
 * "basic-annual"
 * "basic-annual-2021"
 * "basic-monthly"
 * "founders-annual"
 * "free-plan-monthly"
 * "internal-base"
 * "internal-pro"
 * "legacy-base-basic-annual"
 * "lite-automation"
 * "premium-annual"
 * "premium-monthly"
 * "pro"
 * "pro-annual"
 * "pro-founders"
 * "pro-legacy"
 * "pro-legacy-annual"
 * "pro-plan-annual"
 * "pro-plan-monthly"
 */

export default {
    mixins: [FeatureablesMixin],

    computed: {
        planId() {
            if (!this.isSubscription) return '';

            return this.subscription.plan.id;
        },

        subscriptionPeriodEndsAt() {
            if (!this.isSubscription) return 0;

            return this.subscription.period_ends_at;
        },

        planSharedInboxesPlanId() {
            if (!this.isSubscription) return '';

            return this.subscription.plan.shared_inboxes_plan_id;
        },

        subscriptionStatus() {
            if (!this.isSubscription) return '';

            return this.subscription.status;
        },

        subscriptionPlanActive() {
            if (!this.isSubscription) return false;

            return this.subscription.plan.active;
        },

        subscriptionPlanName() {
            if (!this.isSubscription) return '';
            if (this.isOnTrial) return 'Trial (Monthly)';

            return this.subscription.plan.name;
        },

        isHideMms() {
            if (!this.isSubscription) return false;
            return Boolean(this.planId === 'pro-monthly-250' || this.checkPlanBasic);
        },

        isSubscription() {
            return Boolean(this.subscription);
        },

        isSubscriptionSeats() {
            if (!this.isSubscription) return false;

            return Boolean(this.subscription.seats);
        },

        isTwilioNumberVendor() {
            if (!this.organization) return false;

            return this.organization.number_vendor?.key === 'twilio-integration';
        },

        isSubscriptionNumbers() {
            if (!this.isSubscription) return false;

            return Boolean(this.subscription.numbers);
        },

        isOrganizationOwner() {
            return this.user.id === this.organization.owner_id;
        },

        isAgency() {
            if (!this.organization) return false;

            return this.organization.is_agency === true;
        },

        isClient() {
            if (!this.organization) return false;

            return Boolean(this.organization?.agency_id);
        },

        hasAccessibleOrganizations() {
            return this.user.hasAccessibleOrganizations === true;
        },

        isAgencyWithSubscription() {
            return this.isAgency && this.subscription;
        },

        isAgencyWithoutSubscription() {
            return (
                this.isAgency &&
                (!this.subscription ||
                    !['active', 'trialing', 'past_due'].includes(this.subscription.status))
            );
        },

        isOnTrial() {
            if (!this.isSubscription) return false;

            return Boolean(
                this.organization?.trial_ends_at !== null && this.subscription.status === 'trialing'
            );
        },

        isCancelled() {
            if (!this.isSubscription) return false;

            return ['canceled', 'incomplete_expired'].includes(this.subscription.status);
        },

        isCanceledOrTrial() {
            return this.isOnTrial || this.isCancelled;
        },

        isSuspended() {
            if (!this.isSubscription) return false;

            return this.subscription.status === 'suspended';
        },

        isPayg() {
            if (!this.isSubscription) return false;

            return this.subscription.scheduled_price_name === 'Free Monthly';
        },

        isUnpaid() {
            if (!this.isSubscription) return false;

            return ['unpaid', 'past_due'].includes(this.subscription.status);
        },

        doubleOptInEnabled() {
            return this.organization.double_opt_in && this.organization.double_opt_in.is_active;
        },

        trialDays() {
            return window.moment.utc(this.organization.trial_ends_at).local().toNow(true);
        },

        trialCredits() {
            if (!this.isSubscription) return false;

            return this.organization.trial_credits;
        },

        isContract() {
            return !!this.organization?.is_contract;
        },

        initialTrialCredits() {
            if (!this.isSubscription) return false;

            return this.subscription.trial_credits;
        },

        isOutOfTrialCredits() {
            return this.organization && this.organization.trial_credits <= 0;
        },

        isOutOfMoney() {
            if (!this.isSubscription) return false;

            if (!this.organization) {
                return false;
            }

            if (this.subscription.plan.active && this.organization.account_credits <= 0) {
                return true;
            } else if (!this.subscription.plan.active && this.organization.account_balance <= 0) {
                return true;
            }

            return false;
        },

        /**
         * Returns this Organization's Subscription Plan's Credits
         *
         * @returns {Number}
         */
        subscriptionPlanCredits() {
            if (!this.isSubscription) return false;

            if (this.subscription.plan.meta && this.subscription.plan.meta.credits) {
                return this.subscription.plan.meta.credits;
            }

            if (this.subscription.plan.interval === 'annual') {
                return 9000;
            }

            return 750;
        },

        /**
         * Returns the cost of this Organization's Subscription Plan's SMS rate.
         *
         * @returns {Number}
         */
        subscriptionPlanSmsRate() {
            if (!this.isSubscription) return false;

            return this.subscription.plan.rates.us_pricing.sms;
        },

        isPlanPro() {
            return !!(this.planId.startsWith('pro') || this.planId.endsWith('pro'));
        },

        isPlanPremium() {
            return !!(this.planId.startsWith('premium') || this.planId.endsWith('premium'));
        },

        isAllowedBasicAnnual() {
            if (!this.isSubscription) return false;

            return Boolean(
                !this.isOnTrial &&
                    [
                        'base',
                        'basic-monthly',
                        'lite-automation',
                        'legacy-base-basic-annual',
                        'founders-annual',
                        'basic-annual',
                        'internal-base',
                        'basic-annual-2021',
                    ].includes(this.planId)
            );
        },

        isResetPlan() {
            return Boolean(
                ['pro-plan-annual', 'premium-annual', 'basic-annual-2021'].includes(this.planId)
            );
        },

        checkPlanFreeAndTrial() {
            return this.checkPlanFree || this.isOnTrial;
        },

        checkPlanFree() {
            return this.planId === 'free-plan-trial' || this.planId === 'free-plan-monthly';
        },

        checkPlanFreeTrial() {
            return this.planId === 'free-plan-trial';
        },

        checkPlanFreeMonthly() {
            return this.planId === 'free-plan-monthly';
        },

        checkBasicAnnual() {
            return ['basic-annual-2021'].includes(this.planId);
        },

        checkProPlanAnnual() {
            return this.planId === 'pro-plan-annual';
        },

        checkPremiumAnnual() {
            return this.planId === 'premium-annual';
        },

        checkPlanBasic() {
            return this.planId.includes('basic');
        },

        checkPlanLegacy() {
            return this.planId.includes('legacy');
        },

        checkPremiumMonthly() {
            return this.planId === 'premium-monthly';
        },

        checkProPlanMonthly() {
            if (!this.isSubscription) return false;

            return this.subscription.status !== 'trialing' && this.planId === 'pro-plan-monthly';
        },

        checkLegacyAccess() {
            return Boolean(
                this.planId === 'base' ||
                    this.planId === 'basic-annual' ||
                    this.planId === 'basic-annual-2021' ||
                    this.planId === 'basic-monthly' ||
                    this.planId === 'founders-annual' ||
                    this.planId === 'internal-base' ||
                    this.planId === 'lite-automation'
            );
        },

        hasNewPlans() {
            // TODO: need check
            return (
                this.planId === 'free-plan-monthly' ||
                this.planId === window.Config.flexible_plan_monthly ||
                this.planId === window.Config.flexible_plan_annual
            );
        },

        hasTrialExpiration() {
            return (
                !this.isCancelled &&
                !this.isSuspended &&
                !this.isUnpaid &&
                this.isOnTrial &&
                !this.isOutOfTrialCredits &&
                this.numberVendor.isBillable
            );
        },

        getNameDefaultPlan() {
            if (!this.subscription) {
                return 'monthly';
            }

            if (
                this.subscription.plan.interval === 'yearly' ||
                this.subscription.plan.interval === 'annual'
            ) {
                return 'annual';
            }

            return this.subscription.plan.interval;
        },

        getDefaultPlanCredit() {
            if (!this.isSubscription) return 0;
            if (this.getNameDefaultPlan === 'annual') {
                return this.subscription.plan_credits / 12;
            } else {
                return Number(this.subscription.plan_credits);
            }
        },

        proPlanMonthly() {
            return this.billingPlans?.monthly;
        },

        proPlanAnnual() {
            return this.billingPlans?.annual;
        },

        proPlanMonthlyTiersForUsers() {
            return this.proPlanMonthly
                .slice(1, 17)
                .filter((item) => item.for_users && item.credits !== null && item.credits !== 0);
        },

        proPlanMonthlyTiersForClients() {
            return this.proPlanMonthly
                .slice(0, 9)
                .filter((item) => item.for_clients && item.credits !== null && item.credits !== 0);
        },

        proPlanMonthlyTiersForSelfUpgrade() {
            return this.proPlanMonthly
                .slice(8, 17)
                .filter((item) => item.credits !== null && item.credits !== 0);
        },

        proPlanAnnualTiersForUsers() {
            return this.proPlanAnnual
                .slice(1, 17)
                .filter((item) => item.for_users && item.credits !== null && item.credits !== 0);
        },

        proPlanAnnualTiersForClients() {
            return this.proPlanAnnual
                .slice(0, 9)
                .filter((item) => item.for_clients && item.credits !== null && item.credits !== 0);
        },

        proPlanAnnualTiersForSelfUpgrade() {
            return this.proPlanAnnual
                .slice(8, 17)
                .filter((item) => item.credits !== null && item.credits !== 0);
        },

        getDefaultPlan() {
            const plans = [...this.proPlanMonthly, ...this.proPlanAnnual];

            return plans.find((item) => item.id === this.planId);
        },

        filterPriceDefaultPlan() {
            if (!this.checkOldBillingPlans) {
                return this.getDefaultPlanArr[0].tiers.filter(
                    (o) => o.up_to === this.getDefaultPlanCredit
                );
            }
        },

        checkOldBillingPlans() {
            if (!this.getDefaultPlan) return true;

            switch (this.subscription.plan.id) {
                case 'base':
                case 'lite-automation':
                case 'basic-monthly':
                case 'internal-base':
                case 'internal-pro':
                case 'pro':
                case 'pro-founders':
                case 'pro-legacy':
                case 'pro-plan-monthly':
                case 'premium-monthly':
                case 'pro-legacy-annual':
                case 'pro-plan-annual':
                case 'pro-annual':
                case 'premium-annual':
                case 'legacy-base-basic-annual':
                case 'founders-annual':
                case 'basic-annual':
                case 'basic-annual-2021':
                case 'free-plan-monthly':
                case 'free-plan-trial':
                case 'trial':
                case 'free-trial':
                    return true;
                default:
                    return false;
            }
        },

        hasNotBillingSources() {
            return !this.billingSources.length;
        },

        isDemoOrganization() {
            return !!this.organization.is_demo;
        },

        itemsUsed() {
            return [
                ...(Array.isArray(this.members) ? this.members : []),
                ...(Array.isArray(this.invitations) ? this.invitations : []),
            ];
        },

        usedSeatsCount() {
            const usedMembers = (Array.isArray(this.members) ? this.members : []).filter(
                (item) => !item.isOwnerRole && !item.isViewOnlyRole
            ).length;
            const usedInvitations = (
                Array.isArray(this.invitations) ? this.invitations : []
            ).filter((item) => !item.isOwnerRole && !item.isViewOnlyRole).length;
            return usedMembers + usedInvitations;
        },

        hasMms() {
            return Boolean(this.number.mms || this.inboxes[this.currentInboxId].number.mms);
        },

        hasUploadFiles() {
            return Boolean(
                this.userPermissions.can_manage_conversations
                // && this.hasFeature('message-send-mms')
                // && this.hasMms
            );
        },

        hasAffiliateLink() {
            return Boolean(this.organization.hasAffiliateLink);
        },

        ...mapState(stores.User, ['user', 'userSettings', 'isDeveloper', 'userPermissions']),
        ...mapState(stores.Organization, ['organization', 'organizationSettings', 'featureFlags']),
        ...mapState(stores.Subscriptions, ['subscription']),
        ...mapState(stores.BillingSources, ['billingPlans']),
        ...mapState(stores.Members, ['members']),
        ...mapState(stores.Invitations, ['invitations']),
        ...mapState(stores.Number, ['number', 'numberVendor']),
        ...mapState(stores.Inboxes, ['inboxes', 'currentInboxId']),
    },

    methods: {
        /**
         * Calculate used seats excluding specific member and/or invitation
         * @param {Object} options - { excludeMemberId, excludeInvitationId }
         * @returns {number}
         */
        calculateUsedSeats({ excludeMemberId = null, excludeInvitationId = null } = {}) {
            const usedMembers = (Array.isArray(this.members) ? this.members : []).filter(
                (item) => item.id !== excludeMemberId && !item.isOwnerRole && !item.isViewOnlyRole
            ).length;
            const usedInvitations = (
                Array.isArray(this.invitations) ? this.invitations : []
            ).filter(
                (item) =>
                    item.id !== excludeInvitationId && !item.isOwnerRole && !item.isViewOnlyRole
            ).length;
            return usedMembers + usedInvitations;
        },

        credits(pennies) {
            let perRate = get(
                this.subscription.plan.rates,
                `${toLower(this.organization.country)}_pricing.sms`
            );

            return Math.floor(pennies / perRate);
        },

        creditsToMoney(credits) {
            if (this.subscription.plan.custom || this.subscription.plan.id === 'pro-yearly-9000') {
                return Math.floor(credits * this.subscription.plan.additional_credit_amount * 100);
            } else if (!this.checkOldBillingPlans) {
                return Math.floor(credits * this.getDefaultPlan.additional_credit_amount);
            } else {
                let perRate = get(
                    this.subscription.plan.rates,
                    `${toLower(this.organization.country)}_pricing.sms`
                );
                return Math.floor(credits * perRate);
            }
        },

        async upgradeOrganization(
            form,
            startCb = () => {},
            finishCb = () => {},
            cancelCb = () => {},
            delayStatus = false
        ) {
            try {
                startCb();

                await this.$store.dispatch('upgradeBillingUpgrade', form);

                finishCb();

                if (delayStatus) {
                    setTimeout(() => {
                        cancelCb();
                        this.close(false);
                    }, 1500);
                } else {
                    finishCb();
                    this.close(false);
                }
            } catch (e) {
                if (errors.response.data.card_error) {
                    form.errors.record({ form: [errors.response.data.card_error] });
                } else if (errors.response.status !== 422) {
                    form.errors.record({ form: ['Something went wrong, please try again.'] });
                }

                finishCb();
                cancelCb();
            }
        },
    },
};
